// 浏览器降级连点器：只向可访问的同源 DOM 派发非 isTrusted 事件。
// onTick 只代表事件已发送，不代表游戏接受或完成了操作；不能跨域，也不是系统点击。
// 百分比点位相对于根 iframe 的内容视口。canvas 内的语义无法由 DOM 检查辨认。

const IDENTITY_EVENTS = ['pagehide', 'beforeunload', 'hashchange', 'popstate']
const FORBIDDEN_TAGS = new Set(['A', 'AREA', 'FORM', 'INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'LABEL', 'IFRAME', 'FRAME'])
const SENSITIVE_TEXT = /支付|付款|充值|下单|购买|结算|订单|兑换|转账|提现|扣款|绑定|解绑|换绑|登录|登出|退出|注销|删除|密码|账号|帐号|实名|授权|同意|提交|导航|返回|跳转|\b(pay(?:ment)?|checkout|purchase|buy|recharge|withdraw|transfer|delete|logout|login|account|password|unbind|bind|confirm|submit|navigate|back|sign[ -]?(?:in|out|up))\b/i

function failure(reason, index) { return { ok: false, reason, ...(index == null ? {} : { index }) } }
function attr(el, name) { return el && el.getAttribute ? el.getAttribute(name) : null }
function parentOf(el) {
  if (el.parentElement) return el.parentElement
  const root = el.getRootNode && el.getRootNode()
  return root && root.host || null
}

function forbiddenTarget(target) {
  if (!target || target.isConnected === false) return 'no-target'
  if (['HTML', 'BODY'].includes(target.tagName)) return 'no-target'
  if (target.tagName && target.tagName.includes('-') && !target.shadowRoot) return 'unsupported'
  let el = target
  while (el && el.nodeType === 1) {
    const role = attr(el, 'role')
    if (FORBIDDEN_TAGS.has(el.tagName) || ['link', 'textbox', 'combobox'].includes(role)
      || el.isContentEditable || (attr(el, 'contenteditable') != null && attr(el, 'contenteditable') !== 'false')
      || el.disabled || attr(el, 'disabled') != null || attr(el, 'aria-disabled') === 'true'
      || attr(el, 'inert') != null || attr(el, 'form') != null) return 'sensitive-target'
    const labels = ['aria-label', 'title', 'name', 'id', 'data-action'].map(name => attr(el, name) || '').join(' ')
    const text = String(el.textContent || '').trim()
    const localText = el === target || el.tagName === 'BUTTON' || role === 'button' || (el.children && el.children.length <= 1)
    const description = `${labels} ${localText && text.length <= 160 ? text : ''}`.replace(/([a-z])([A-Z])/g, '$1 $2')
    if (SENSITIVE_TEXT.test(description)) return 'sensitive-target'
    el = parentOf(el)
  }
  return ''
}

function frameSnapshot(frame) {
  if (!frame || frame.isConnected === false || !frame.getBoundingClientRect) return failure('no-target')
  for (let parent = frame; parent; parent = parentOf(parent)) {
    if (parent.inert || attr(parent, 'inert') != null) return failure('sensitive-target')
  }
  let win
  let doc
  try {
    win = frame.contentWindow
    doc = win && win.document
    if (!doc || !doc.elementFromPoint) return failure('unsupported')
    // 读取 document/location 只允许同源；不尝试绕过 sandbox 或跨域限制。
    const href = win.location && win.location.href || ''
    const rect = frame.getBoundingClientRect()
    const owner = frame.ownerDocument && frame.ownerDocument.defaultView
    const style = owner && owner.getComputedStyle && owner.getComputedStyle(frame)
    if (style && (style.visibility === 'hidden' || style.display === 'none' || style.pointerEvents === 'none')) return failure('no-target')
    const viewportWidth = Number(win.innerWidth || doc.documentElement && doc.documentElement.clientWidth)
    const viewportHeight = Number(win.innerHeight || doc.documentElement && doc.documentElement.clientHeight)
    const offsetWidth = Number(frame.offsetWidth || rect.width)
    const offsetHeight = Number(frame.offsetHeight || rect.height)
    const clientWidth = Number(frame.clientWidth || offsetWidth)
    const clientHeight = Number(frame.clientHeight || offsetHeight)
    if (![rect.width, rect.height, viewportWidth, viewportHeight, offsetWidth, offsetHeight, clientWidth, clientHeight].every(value => Number.isFinite(value) && value > 0)) return failure('no-target')
    return { ok: true, frame, win, doc, href, src: attr(frame, 'src'), srcdoc: attr(frame, 'srcdoc'), viewportWidth, viewportHeight,
      rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
      offsetWidth, offsetHeight, clientWidth, clientHeight, borderLeft: Number(frame.clientLeft || 0), borderTop: Number(frame.clientTop || 0) }
  } catch (error) {
    return failure('cross-origin')
  }
}

function sameLayout(a, b) {
  return ['viewportWidth', 'viewportHeight', 'offsetWidth', 'offsetHeight', 'clientWidth', 'clientHeight', 'borderLeft', 'borderTop'].every(key => a[key] === b[key])
    && ['left', 'top', 'width', 'height'].every(key => Math.abs(a.rect[key] - b.rect[key]) < .5)
}

function hitInFrame(snapshot, x, y, path) {
  if (path.length > 12) return failure('unsupported')
  let target
  try {
    target = snapshot.doc.elementFromPoint(x, y)
    // 支持开放的 Shadow DOM；闭合 shadow root 无法可靠验证目标，宿主仍按普通 DOM 检查。
    for (let depth = 0; target && target.shadowRoot && target.shadowRoot.elementFromPoint && depth < 12; depth++) {
      const next = target.shadowRoot.elementFromPoint(x, y)
      if (!next || next === target) break
      target = next
    }
  } catch (error) { return failure('unsupported') }
  if (!target) return failure('no-target')
  if (target.tagName === 'IFRAME' || target.tagName === 'FRAME') {
    const child = frameSnapshot(target)
    if (!child.ok) return child
    const scaleX = child.rect.width / child.offsetWidth
    const scaleY = child.rect.height / child.offsetHeight
    const contentX = x - child.rect.left - child.borderLeft * scaleX
    const contentY = y - child.rect.top - child.borderTop * scaleY
    const contentWidth = child.clientWidth * scaleX
    const contentHeight = child.clientHeight * scaleY
    if (contentX < 0 || contentY < 0 || contentX >= contentWidth || contentY >= contentHeight) return failure('no-target')
    return hitInFrame(child, contentX / contentWidth * child.viewportWidth, contentY / contentHeight * child.viewportHeight, [...path, child])
  }
  const reason = forbiddenTarget(target)
  if (reason) return failure(reason)
  return { ok: true, target, win: snapshot.win, doc: snapshot.doc, x, y, path }
}

function resolvePoint(frame, point) {
  const snapshot = frameSnapshot(frame)
  if (!snapshot.ok) return snapshot
  return hitInFrame(snapshot, Math.min(snapshot.viewportWidth - .5, snapshot.viewportWidth * point.x / 100),
    Math.min(snapshot.viewportHeight - .5, snapshot.viewportHeight * point.y / 100), [snapshot])
}

function eventMode(hit) {
  const win = hit.win
  const mobileCanvas = hit.target.tagName === 'CANVAS' && win.navigator && win.navigator.maxTouchPoints > 0
  if (mobileCanvas && typeof win.Touch === 'function' && typeof win.TouchEvent === 'function') return 'touch'
  if (typeof win.MouseEvent !== 'function') return ''
  return typeof win.PointerEvent === 'function' ? 'pointer-mouse' : 'mouse'
}

function sendClick(hit, stillCurrent) {
  const { target, win, x, y } = hit
  const mode = eventMode(hit)
  if (!mode) return failure('unsupported')
  const base = { bubbles: true, cancelable: true, composed: true, view: win, clientX: x, clientY: y, button: 0, detail: 1 }
  const emit = event => {
    const reason = stillCurrent() || forbiddenTarget(target)
    if (reason) return failure(reason)
    return { ok: true, accepted: target.dispatchEvent(event) }
  }
  try {
    if (mode === 'touch') {
      const touch = new win.Touch({ identifier: 1, target, clientX: x, clientY: y, pageX: x + (win.scrollX || 0), pageY: y + (win.scrollY || 0), radiusX: 1, radiusY: 1, force: 1 })
      const down = emit(new win.TouchEvent('touchstart', { ...base, touches: [touch], targetTouches: [touch], changedTouches: [touch] }))
      if (!down.ok) return down
      const up = emit(new win.TouchEvent('touchend', { ...base, touches: [], targetTouches: [], changedTouches: [touch] }))
      return up.ok ? { ok: true, mode, synthetic: true } : up
    }
    let mouseCompatibility = true
    if (mode === 'pointer-mouse') {
      const down = emit(new win.PointerEvent('pointerdown', { ...base, pointerId: 1, pointerType: 'mouse', isPrimary: true, buttons: 1, pressure: .5, width: 1, height: 1 }))
      if (!down.ok) return down
      mouseCompatibility = down.accepted
    }
    if (mouseCompatibility) {
      const down = emit(new win.MouseEvent('mousedown', { ...base, buttons: 1 }))
      if (!down.ok) return down
    }
    if (mode === 'pointer-mouse') {
      const up = emit(new win.PointerEvent('pointerup', { ...base, pointerId: 1, pointerType: 'mouse', isPrimary: true, buttons: 0, pressure: 0, width: 1, height: 1 }))
      if (!up.ok) return up
    }
    if (mouseCompatibility) {
      const up = emit(new win.MouseEvent('mouseup', { ...base, buttons: 0 }))
      if (!up.ok) return up
    }
    // 只派发一次 click，不再调用 target.click()，也不再叠加 touch 事件。
    const clicked = emit(new win.MouseEvent('click', { ...base, buttons: 0 }))
    return clicked.ok ? { ok: true, mode, synthetic: true } : clicked
  } catch (error) { return failure('unsupported') }
}

export function createBrowserClicker({ getFrame, getPoints, getInterval, onTick = () => {}, onStop = () => {} }) {
  let running = false
  let destroyed = false
  let timer = null
  let hostWindow = null
  let rootFrame = null
  let config = null
  let frames = []
  let index = 0
  let count = 0
  let session = 0
  const removers = []

  function readConfig() {
    const raw = getPoints()
    const interval = Number(getInterval())
    if (!Array.isArray(raw) || !raw.length || raw.length > 6 || raw.some(point => !point)
      || ![200, 500, 1000, 2000].includes(interval)) return null
    const points = raw.map(point => ({ x: Number(point && point.x), y: Number(point && point.y) }))
    if (points.some(point => !Number.isFinite(point.x) || !Number.isFinite(point.y) || point.x < 0 || point.x > 100 || point.y < 0 || point.y > 100)) return null
    return { points, interval, key: JSON.stringify([points, interval]) }
  }
  function listen(target, event, handler) {
    if (!target || !target.addEventListener) return
    target.addEventListener(event, handler)
    removers.push(() => target.removeEventListener(event, handler))
  }
  function stop(reason = 'manual') {
    const wasRunning = running
    running = false
    session++
    if (timer != null && hostWindow) hostWindow.clearTimeout(timer)
    timer = null
    removers.splice(0).forEach(remove => remove())
    frames = []
    if (wasRunning) onStop(reason)
  }
  function environmentReason() {
    if (!running || destroyed) return 'stopped'
    if (getFrame() !== rootFrame) return 'navigation'
    if (hostWindow.document && hostWindow.document.hidden) return 'hidden'
    const latestConfig = readConfig()
    if (!latestConfig || latestConfig.key !== config.key) return 'configuration-changed'
    for (const previous of frames) {
      const current = frameSnapshot(previous.frame)
      if (!current.ok) return current.reason
      if (current.doc !== previous.doc || current.href !== previous.href || current.src !== previous.src || current.srcdoc !== previous.srcdoc) return 'navigation'
      if (current.doc.hidden) return 'hidden'
      if (!sameLayout(previous, current)) return 'resize'
    }
    return ''
  }
  function tick() {
    timer = null
    if (!running) return
    const activeSession = session
    try {
      const reason = environmentReason()
      if (reason) { stop(reason); return }
      const hit = resolvePoint(rootFrame, config.points[index])
      if (!hit.ok) { stop(hit.reason); return }
      if (hit.path.some(item => !frames.some(known => known.frame === item.frame && known.doc === item.doc))) { stop('navigation'); return }
      const result = sendClick(hit, () => {
        if (session !== activeSession) return 'stopped'
        const environment = environmentReason()
        if (environment) return environment
        const current = resolvePoint(rootFrame, config.points[index])
        if (!current.ok) return current.reason
        return current.target === hit.target && current.doc === hit.doc ? '' : 'no-target'
      })
      if (!result.ok) { if (session === activeSession) stop(result.reason); return }
      if (session !== activeSession) return
      count++
      onTick({ ...result, index, count })
      index = (index + 1) % config.points.length
      if (running && session === activeSession) timer = hostWindow.setTimeout(tick, config.interval)
    } catch (error) { stop('unsupported') }
  }
  function start() {
    if (destroyed) return failure('destroyed')
    if (running) return { ok: true, alreadyRunning: true }
    try {
      config = readConfig()
      if (!config) return failure('invalid-configuration')
      rootFrame = getFrame()
      hostWindow = rootFrame && rootFrame.ownerDocument && rootFrame.ownerDocument.defaultView
      if (!hostWindow || !hostWindow.setTimeout) return failure('unsupported')
      if (hostWindow.document && hostWindow.document.hidden) return failure('hidden')
      const knownFrames = new Map()
      for (let i = 0; i < config.points.length; i++) {
        const hit = resolvePoint(rootFrame, config.points[i])
        if (!hit.ok) return failure(hit.reason, i)
        if (!eventMode(hit)) return failure('unsupported', i)
        for (const snapshot of hit.path) {
          if (snapshot.doc.hidden) return failure('hidden', i)
          knownFrames.set(snapshot.frame, snapshot)
        }
      }
      frames = [...knownFrames.values()]
      count = 0
      index = 0
      running = true
      session++
      const hidden = () => { if ([hostWindow.document, ...frames.map(item => item.doc)].some(doc => doc && doc.hidden)) stop('hidden') }
      const resize = () => stop('resize')
      const navigation = () => stop('navigation')
      listen(hostWindow.document, 'visibilitychange', hidden)
      listen(hostWindow, 'resize', resize)
      listen(hostWindow.visualViewport, 'resize', resize)
      IDENTITY_EVENTS.forEach(event => listen(hostWindow, event, navigation))
      for (const snapshot of frames) {
        listen(snapshot.frame, 'load', navigation)
        listen(snapshot.doc, 'visibilitychange', hidden)
        listen(snapshot.win, 'resize', resize)
        listen(snapshot.win.visualViewport, 'resize', resize)
        IDENTITY_EVENTS.forEach(event => listen(snapshot.win, event, navigation))
      }
      timer = hostWindow.setTimeout(tick, config.interval)
      return { ok: true, synthetic: true }
    } catch (error) {
      stop('unsupported')
      return failure('unsupported')
    }
  }
  return { start, stop, isRunning: () => running, destroy() { destroyed = true; stop('destroyed') } }
}

// One step for the task runner; retain the same DOM checks as continuous clicking.
export function clickGamePoint(frame, point) {
  const hit = resolvePoint(frame, { x: point.x * 100, y: point.y * 100 })
  if (!hit.ok) return hit
  return sendClick(hit, () => {
    for (const saved of hit.path) {
      const current = frameSnapshot(saved.frame)
      if (!current.ok) return current.reason
      if (current.doc !== saved.doc || current.href !== saved.href) return 'navigation'
      if (!sameLayout(saved, current)) return 'resize'
    }
    return ''
  })
}

// Used by the script embedded in the final game document, not by the outer iframe.
export function clickDocumentPoint(win, point) {
  const doc = win.document
  const hit = hitInFrame({ win, doc }, point.x * (win.innerWidth - .5), point.y * (win.innerHeight - .5), [])
  if (!hit.ok) return hit
  return sendClick(hit, () => win.document !== doc || doc.hidden ? 'navigation' : '')
}
